/**
 * Tab "Nội dung" — shows lesson content from CourseLesson template or ad-hoc lesson.
 * Uses whitespace-pre-wrap for content rendering (no react-markdown dependency needed).
 */
import { BookOpen } from 'lucide-react';
import type { DashboardLesson } from '@client/lib/queries/use-session-dashboard';

interface TabContentProps {
  lesson?: DashboardLesson | null;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  );
}

function PreText({ text }: { text: string }) {
  return (
    <p className="whitespace-pre-wrap rounded-md bg-muted/50 px-3 py-2 text-sm leading-relaxed">
      {text}
    </p>
  );
}

export function TabContent({ lesson }: TabContentProps) {
  if (!lesson) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <BookOpen className="h-8 w-8 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">Buổi học này chưa được liên kết với bài học.</p>
      </div>
    );
  }

  const tpl = lesson.template;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-base font-semibold text-foreground">
          {tpl ? tpl.title : lesson.title}
        </h2>
        {(tpl?.description ?? lesson.description) && (
          <p className="mt-1 text-sm text-muted-foreground">
            {tpl?.description ?? lesson.description}
          </p>
        )}
      </div>

      {/* Template-sourced fields */}
      {tpl ? (
        <>
          {tpl.content && (
            <Section title="Nội dung bài học">
              <PreText text={tpl.content} />
            </Section>
          )}
          {tpl.learningObjectives && (
            <Section title="Mục tiêu học tập">
              <PreText text={tpl.learningObjectives} />
            </Section>
          )}
          {tpl.teacherNotes && (
            <Section title="Ghi chú cho GV">
              <PreText text={tpl.teacherNotes} />
            </Section>
          )}
          {tpl.homeworkSummary && (
            <Section title="Bài tập về nhà">
              <PreText text={tpl.homeworkSummary} />
            </Section>
          )}
          {!tpl.content && !tpl.learningObjectives && !tpl.teacherNotes && !tpl.homeworkSummary && (
            <p className="text-sm text-muted-foreground">Chưa có nội dung chi tiết.</p>
          )}
        </>
      ) : (
        // Ad-hoc lesson — just show description
        !lesson.description && (
          <p className="text-sm text-muted-foreground">Chưa có nội dung chi tiết.</p>
        )
      )}
    </div>
  );
}
